import React from "react";

import { formatNumber, useTranslation } from "utils";

import { Button, Skeleton } from "components";

import { NO_FIGURE } from "pages/app/home/status-cards/StatusCard";
import { formatHectares } from "pages/app/home/status-cards/homeFieldStats";

type FarmMapStatsBarProps = {
  fieldCount: number;
  totalHectares: number;
  cropCount: number;
  /** the field practices behind the crop count are still loading */
  cropCountLoading: boolean;
  /** the field practices failed to load: the crop count can't be shown */
  cropCountUnavailable?: boolean;
  cropYear: number;
  /** no handler: the farm's program has no fields page to open */
  onViewFields?: () => void;
};

export const FarmMapStatsBar = ({
  fieldCount,
  totalHectares,
  cropCount,
  cropCountLoading,
  cropCountUnavailable = false,
  cropYear,
  onViewFields,
}: FarmMapStatsBarProps) => {
  const { t } = useTranslation("home");

  const stats = [
    { id: "fields", value: formatNumber({ number: fieldCount, decimalPlaces: 0 }), label: t("farmMap.stats.fields"), loading: false },
    {
      id: "hectares",
      value: t("farmMap.stats.hectares", { value: formatHectares(totalHectares) }),
      label: t("farmMap.stats.totalArea"),
      loading: false,
    },
    {
      id: "crops",
      value: formatNumber({ number: cropCount, decimalPlaces: 0 }),
      label: t("farmMap.stats.crops", { year: cropYear }),
      loading: cropCountLoading,
      unavailable: cropCountUnavailable,
    },
  ];

  return (
    <div className="farm-map-bar farm-map-stats-bar" data-testid="farm-map-stats-bar">
      {stats.map((stat) => (
        <span key={stat.id} className="farm-map-stat" data-testid={`farm-map-stat-${stat.id}`}>
          {stat.loading ? (
            <span className="farm-map-stat-value" data-testid={`farm-map-stat-${stat.id}-loading`}>
              <Skeleton type="block" height="1.5rem" width="2rem" />
            </span>
          ) : stat.unavailable ? (
            <span className="farm-map-stat-value">
              <span aria-hidden="true">{NO_FIGURE}</span>
              <span className="farm-map-stat-hidden-text">{t("statusCards.unavailable")}</span>
            </span>
          ) : (
            <span className="farm-map-stat-value">{stat.value}</span>
          )}
          <span className="farm-map-stat-label">{stat.label}</span>
        </span>
      ))}
      {onViewFields ? (
        <Button dataTestId="farm-map-view-fields" type="secondary" size="small" onClick={onViewFields}>
          {t("farmMap.stats.viewFields")}
        </Button>
      ) : null}
    </div>
  );
};
